import { useEffect, useMemo, useRef, useState } from "react";
import { getStrokes } from "@/data/strokes";

/**
 * StrokeGuide
 * Animates each stroke sequentially using stroke-dashoffset, then leaves the
 * full guide visible as a faint outline for tracing.
 *
 * Props:
 *  - guideText: string (e.g., "A", "Circle")
 *  - playSignal: number — increment to (re)play the animation
 *  - showNumbers: bool — render numbered start markers when not animating
 *  - onDone: () => void
 */
export default function StrokeGuide({ guideText, playSignal = 0, showNumbers = true, onDone }) {
  const paths = useMemo(() => getStrokes(guideText), [guideText]);
  const refs = useRef([]);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (!paths) return;
    // Initial: show full faint outline
    refs.current.forEach((p) => {
      if (!p) return;
      p.style.transition = "none";
      p.style.strokeDasharray = "none";
      p.style.strokeDashoffset = "0";
    });
  }, [paths]);

  useEffect(() => {
    if (!paths || playSignal === 0) return;
    const timers = [];
    setAnimating(true);
    // Reset to invisible
    refs.current.forEach((p) => {
      if (!p) return;
      const len = p.getTotalLength();
      p.style.transition = "none";
      p.style.strokeDasharray = `${len}`;
      p.style.strokeDashoffset = `${len}`;
    });
    // Schedule sequential reveal
    let cumulative = 0;
    refs.current.forEach((p, i) => {
      if (!p) return;
      const len = p.getTotalLength();
      const dur = Math.max(450, len * 12); // ms
      timers.push(
        setTimeout(() => {
          p.style.transition = `stroke-dashoffset ${dur}ms cubic-bezier(.4,.0,.2,1)`;
          p.style.strokeDashoffset = "0";
        }, cumulative)
      );
      cumulative += dur + 220;
    });
    timers.push(
      setTimeout(() => {
        // Clear dasharray so the path stays solid afterwards
        refs.current.forEach((p) => {
          if (!p) return;
          p.style.transition = "none";
          p.style.strokeDasharray = "none";
        });
        setAnimating(false);
        onDone && onDone();
      }, cumulative + 50)
    );
    return () => timers.forEach(clearTimeout);
  }, [playSignal, paths, onDone]);

  if (!paths) return null;

  const startPoints = paths
    .map((d) => {
      const m = d.match(/M\s*(-?\d+\.?\d*)\s*[ ,]\s*(-?\d+\.?\d*)/);
      return m ? { x: parseFloat(m[1]), y: parseFloat(m[2]) } : null;
    })
    .filter(Boolean);

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-full pointer-events-none"
      aria-hidden="true"
    >
      {paths.map((d, i) => (
        <path
          key={d}
          ref={(el) => (refs.current[i] = el)}
          d={d}
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={animating ? 0.65 : 0.22}
        />
      ))}
      {showNumbers && paths.length > 1 &&
        startPoints.map((p, i) => (
          <g key={`marker-${paths[i]}`} transform={`translate(${p.x} ${p.y})`} style={{ opacity: animating ? 0 : 1, transition: "opacity 250ms ease" }}>
            <circle r="4.2" fill="hsl(var(--primary))" />
            <text
              x="0"
              y="1.6"
              textAnchor="middle"
              fontSize="5"
              fontWeight="700"
              fill="white"
              fontFamily="Nunito, system-ui, sans-serif"
            >
              {i + 1}
            </text>
          </g>
        ))}
    </svg>
  );
}
